import { forwardRef, useCallback, useMemo } from "react";
import { Button } from "react-bootstrap";
// utils
import getClasses from "utils/getClasses";
// components
import ActionsMenu from "../../../actions-menu/ActionsMenu";
import TypeIcon from "../../../type-icon";
// styles
import styles from "./styles.module.scss";
// types
import type {
  OrchidDriveDirectory,
  OrchidDriveFile,
} from "contexts/orchid-drive/types";
import type { AttachmentsViewProps } from "components/Dashboard/DoctorDashboard/attachments";
// hooks
import useRenameEntry from "components/Dashboard/DoctorDashboard/attachments/hooks/useRenameEntry";
// hoc
import withEditable from "../../../with-editable";
import { AmplitudePageName } from "utils/amplitude";

type Props = {
  entry: OrchidDriveDirectory | OrchidDriveFile;
  entryId: string;
  type: string | undefined;
  downloadHref?: string;
  viewHref?: string;
  onClickFolder?: () => void;
  checked?: boolean;
  hideActionsMenu?: boolean;
  onDragFilesStart?: () => void;
  onDragFilesEnd?: (arg: OrchidDriveFile | OrchidDriveDirectory) => void;
  isSelectedDragging?: boolean;
  onMouseOver?: (arg: string) => void;
  onMouseOut?: () => void;
  hoverId?: string;
  analyticsPageName: AmplitudePageName;
} & Pick<
  AttachmentsViewProps,
  "batchEntryOps" | "deleteEntry" | "onError" | "onDownload"
> &
  Partial<ReturnType<typeof useRenameEntry>>;

const EditableButton = withEditable(Button);
const EditableDiv = withEditable("div");

const GridItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      checked,
      onClickFolder,
      hideActionsMenu,
      batchEntryOps,
      entry,
      type,
      onError,
      onDownload,
      onDragFilesStart,
      onDragFilesEnd,
      isSelectedDragging,
      // rename
      onRename,
      closeRename,
      showRename,
      openRename,
      // mouse over / hover
      onMouseOver,
      onMouseOut,
      hoverId,
      analyticsPageName,
      ...rest
    },
    ref,
  ) => {
    const handleUpdate = useCallback(
      async (name: string) => {
        if (!name || name === entry.name || !batchEntryOps) {
          return closeRename && closeRename();
        }

        return (
          onRename &&
          onRename(
            {
              name,
              entryId: rest.entryId,
              parentDirectoryId: entry.parentDirectoryId,
            },
            entry.name,
          )
        );
      },
      [
        entry.name,
        entry.parentDirectoryId,
        batchEntryOps,
        onRename,
        rest.entryId,
        closeRename,
      ],
    );

    const isFolder = useMemo(() => type === "folder", [type]);

    return (
      <div
        className={getClasses(
          styles["grid-item"],
          !checked &&
            isSelectedDragging &&
            isFolder &&
            hoverId === rest.entryId &&
            styles["grid-item--folder-dropping"],
        )}
        ref={ref}
      >
        <div
          className={getClasses(
            styles["grid-item__body"],
            checked && styles["grid-item__body--checked"],
            checked &&
              isSelectedDragging &&
              styles["grid-item__body--checked-dragging"],

            !checked &&
              isSelectedDragging &&
              styles["grid-item__body--unchecked-dropping"],
          )}
          onMouseOver={() => {
            if (checked || !isSelectedDragging || !isFolder) {
              return;
            }
            return onMouseOver && onMouseOver(rest.entryId);
          }}
          onMouseOut={onMouseOut}
          onMouseDown={() => {
            if (checked && onDragFilesStart) {
              onDragFilesStart();
            }
          }}
          onMouseUp={() => onDragFilesEnd && onDragFilesEnd(entry)}
        >
          <div className={styles["grid-item__icon"]}>
            <TypeIcon mimeType={type} />
          </div>
          {isFolder ? (
            <EditableButton
              variant={"link"}
              className={
                styles["grid-item__name"] +
                " " +
                styles["grid-item__name--folder"]
              }
              onClick={(e) => {
                e.stopPropagation();
                return onClickFolder && onClickFolder();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              show={showRename === rest.entryId}
              onUpdate={handleUpdate}
            >
              {entry.name}
            </EditableButton>
          ) : (
            <EditableDiv
              className={styles["grid-item__name"]}
              show={showRename === rest.entryId}
              onUpdate={handleUpdate}
            >
              {entry.name}
            </EditableDiv>
          )}
          {!hideActionsMenu && (
            <div className={styles["grid-item__dropdown"]}>
              <ActionsMenu
                {...rest}
                type={type}
                entry={entry}
                onRename={() => openRename && openRename(rest.entryId)}
                onDownload={() =>
                  onDownload &&
                  onDownload([
                    {
                      ...entry,
                      type: type,
                      guid: rest.entryId,
                    },
                  ])
                }
                analyticsPageName={analyticsPageName}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default GridItem;
